import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form,
    Input,
    Button,
    Card,
    Row,
    Col,
    Select,
    Space,
    Typography,
    Alert,
    Divider,
    Spin,
    Statistic,
    Table,
    message,
} from 'antd';
import {
    ArrowLeftOutlined,
    SaveOutlined,
    ExperimentOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
    createGlobalSegment,
    updateGlobalSegment,
    fetchGlobalSegment,
    testGlobalSegment,
    selectCurrentGlobalSegment,
    selectGlobalSegmentsLoading,
    selectGlobalSegmentsError,
    selectGlobalSegmentTestResult,
    setCurrentGlobalSegment,
    clearGlobalSegmentTestResult,
} from 'store/slices/globalSegments.slice';
import { selectApplications } from 'store/slices/applications.slice';
import { SegmentCondition } from 'types/push.types';
import { GlobalSegmentCreateRequest } from 'types/global_segments.types';
import { formatters } from 'utils/formatters';
import { ROUTES } from 'config/routes';
import SegmentConditionsBuilder from '../Segments/SegmentConditionsBuilder';
import { fetchApplications } from 'store/slices/applications.slice';

const { Title, Text } = Typography;
const { TextArea } = Input;

const GlobalSegmentForm: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const [form] = Form.useForm();

    const currentSegment = useAppSelector(selectCurrentGlobalSegment);
    const applications = useAppSelector(selectApplications);
    const isLoading = useAppSelector(selectGlobalSegmentsLoading);
    const error = useAppSelector(selectGlobalSegmentsError);
    const testResult = useAppSelector(selectGlobalSegmentTestResult);

    const isEditMode = Boolean(id);

    const [conditions, setConditions] = useState<SegmentCondition[]>([]);
    const [testAppIds, setTestAppIds] = useState<number[]>([]);
    const [isTesting, setIsTesting] = useState(false);

    useEffect(() => {
        if (isEditMode && id) {
            dispatch(fetchGlobalSegment(Number(id)));
        }

        return () => {
            dispatch(setCurrentGlobalSegment(null));
            dispatch(clearGlobalSegmentTestResult());
        };
    }, [dispatch, id, isEditMode]);

    useEffect(() => {
        if (currentSegment && isEditMode) {
            form.setFieldsValue({
                name: currentSegment.name,
                description: currentSegment.description,
            });
            setConditions(currentSegment.conditions || []);
        }
    }, [currentSegment, isEditMode, form]);

    useEffect(() => {
        dispatch(fetchApplications({ limit: 1000 }));

        if (isEditMode && id) {
            dispatch(fetchGlobalSegment(Number(id)));
        }

        return () => {
            dispatch(setCurrentGlobalSegment(null));
            dispatch(clearGlobalSegmentTestResult());
        };
    }, [dispatch, id, isEditMode]);

    const handleTest = async () => {
        if (conditions.length === 0) {
            message.warning('Добавьте хотя бы одно условие');
            return;
        }
        if (testAppIds.length === 0) {
            message.warning('Выберите хотя бы одно приложение для тестирования');
            return;
        }

        try {
            setIsTesting(true);
            await dispatch(
                testGlobalSegment({
                    app_ids: testAppIds,
                    conditions,
                }),
            ).unwrap();
            message.success('Тест выполнен');
        } catch (err: any) {
            message.error(err.message || err || 'Ошибка тестирования');
        } finally {
            setIsTesting(false);
        }
    };

    const handleSubmit = async (values: any) => {
        if (conditions.length === 0) {
            message.error('Добавьте хотя бы одно условие');
            return;
        }

        const data: GlobalSegmentCreateRequest = {
            name: values.name,
            description: values.description || '',
            conditions,
        };

        try {
            if (isEditMode && id) {
                await dispatch(updateGlobalSegment({ id: Number(id), data })).unwrap();
                message.success('Глобальный сегмент обновлён');
            } else {
                await dispatch(createGlobalSegment(data)).unwrap();
                message.success('Глобальный сегмент создан');
            }
            navigate(ROUTES.GLOBAL_SEGMENTS);
        } catch (err: any) {
            message.error(err.message || err || 'Ошибка сохранения');
        }
    };

    const perAppColumns: ColumnsType<{ appId: number; count: number }> = [
        {
            title: 'Приложение',
            dataIndex: 'appId',
            key: 'appId',
            render: (appId: number) => {
                const app = applications.find(a => a.id === appId);
                return app ? app.name : `App #${appId}`;
            },
        },
        {
            title: 'Пользователей',
            dataIndex: 'count',
            key: 'count',
            align: 'right',
            render: (count: number) => (
                <Text strong>{formatters.number(count)}</Text>
            ),
        },
    ];

    const perAppData = testResult
        ? Object.entries(testResult.per_app).map(([appId, count]) => ({
            appId: Number(appId),
            count: Number(count),
            key: appId,
        }))
        : [];

    if (isLoading && isEditMode && !currentSegment) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" tip="Загрузка сегмента..." />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Row align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(ROUTES.GLOBAL_SEGMENTS)}
                        style={{ marginRight: 16 }}
                    >
                        Назад
                    </Button>
                </Col>
                <Col>
                    <Space>
                        <GlobalOutlined style={{ color: '#722ed1', fontSize: 24 }} />
                        <Title level={2} style={{ margin: 0 }}>
                            {isEditMode
                                ? 'Редактирование глобального сегмента'
                                : 'Создание глобального сегмента'}
                        </Title>
                    </Space>
                </Col>
            </Row>

            {error && (
                <Alert
                    message="Ошибка"
                    description={error}
                    type="error"
                    closable
                    style={{ marginBottom: 24 }}
                />
            )}

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Row gutter={24}>
                    <Col xs={24} lg={16}>
                        <Card title="Основная информация" style={{ marginBottom: 24 }}>
                            <Alert
                                message="Глобальный сегмент"
                                description="Этот сегмент не привязан к конкретному приложению. Условия будут применяться к пользователям всех приложений при использовании в глобальных кампаниях."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />

                            <Form.Item
                                label="Название сегмента"
                                name="name"
                                rules={[
                                    { required: true, message: 'Введите название' },
                                    { min: 3, message: 'Минимум 3 символа' },
                                ]}
                            >
                                <Input
                                    placeholder="Например: VIP пользователи (все приложения)"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item label="Описание" name="description">
                                <TextArea
                                    rows={3}
                                    placeholder="Опишите этот сегмент..."
                                />
                            </Form.Item>
                        </Card>

                        <SegmentConditionsBuilder
                            conditions={conditions}
                            onChange={setConditions}
                        />
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card title="Тестирование" style={{ marginBottom: 24 }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <Text type="secondary">
                                    Выберите приложения и проверьте, сколько пользователей
                                    соответствуют условиям.
                                </Text>

                                <div>
                                    <Text strong style={{ fontSize: 12 }}>
                                        Приложения для теста
                                    </Text>
                                    <Select
                                        mode="multiple"
                                        style={{ width: '100%', marginTop: 4 }}
                                        placeholder="Выберите приложения"
                                        value={testAppIds}
                                        onChange={setTestAppIds}
                                        maxTagCount="responsive"
                                        showSearch
                                        optionFilterProp="label"
                                        options={applications.map(app => ({
                                            value: app.id,
                                            label: app.name,
                                        }))}
                                        dropdownRender={menu => (
                                            <>
                                                <Space style={{ padding: '4px 8px' }}>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={() => setTestAppIds(applications.map(a => a.id))}
                                                    >
                                                        Выбрать все ({applications.length})
                                                    </Button>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={() => setTestAppIds([])}
                                                    >
                                                        Очистить
                                                    </Button>
                                                </Space>
                                                <Divider style={{ margin: '4px 0' }} />
                                                {menu}
                                            </>
                                        )}
                                    />
                                </div>

                                <Button
                                    block
                                    icon={<ExperimentOutlined />}
                                    onClick={handleTest}
                                    loading={isTesting}
                                    disabled={
                                        conditions.length === 0 || testAppIds.length === 0
                                    }
                                >
                                    {isTesting ? 'Тестирование...' : 'Тестировать сегмент'}
                                </Button>

                                {testResult !== null && (
                                    <>
                                        <Divider />
                                        <Statistic
                                            title="Всего пользователей"
                                            value={testResult.total}
                                            valueStyle={{ color: '#722ed1', textAlign: 'center' }}
                                        />

                                        {perAppData.length > 0 && (
                                            <div style={{ marginTop: 12 }}>
                                                <Text
                                                    strong
                                                    style={{
                                                        fontSize: 12,
                                                        display: 'block',
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    Разбивка по приложениям
                                                </Text>
                                                <Table
                                                    columns={perAppColumns}
                                                    dataSource={perAppData}
                                                    pagination={false}
                                                    size="small"
                                                    showHeader={false}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </Space>
                        </Card>

                        <Card title="Действия">
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <Button
                                    block
                                    type="primary"
                                    size="large"
                                    icon={<SaveOutlined />}
                                    htmlType="submit"
                                    loading={isLoading}
                                >
                                    {isEditMode ? 'Обновить сегмент' : 'Создать сегмент'}
                                </Button>

                                <Button
                                    block
                                    onClick={() => navigate(ROUTES.GLOBAL_SEGMENTS)}
                                >
                                    Отмена
                                </Button>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default GlobalSegmentForm;